// src/oura/components/fixes/FixDrawer.tsx
//
// One fix, opened from either Fixes view: why it is on the plan, what it
// moves, what the platform recorded about its effect, the questions and pages
// it is about, and how we prove it is done. The foot changes its status and
// due date through the same handlers as the rows.
//
// It reads; it does not draft. Drafting and publishing live in the Site
// issues drawer (removed from here on purpose in be67e72e, 24 Aug), so a fix
// raised by a finding links out to that finding instead of embedding a second
// copy of the flow. The delivery record lives on History > Activity.

import type { AeoActivityEntry } from "@service/aeo/types";
import type { AuditIssue, Recommendation } from "../../audit";
import { blockLabel, type TaskStatus, type TrackerTask } from "../../tracker";
import { cn } from "../../lib/cn";
import { shortDate } from "../../lib/oura";
import { ContradictionEvidenceBlock, contradictionEvidence } from "../ContradictionEvidence";
import { ArrowLink, Dot, Drawer, DrawerBlock, LATE_TEXT, STAFF_STATUS, TYPE, type StaffStatus } from "@oura/ui";
import { AffectedPages, StatusSelect, TargetDate } from "./TaskParts";
import {
    OWNER_LABEL,
    TASK_STATUS,
    daysLate,
    doneLabel,
    dueLabel,
    impactOf,
    lateLabel,
    movesOf,
    taskNoteText,
    whyLine,
    type PlacedTask,
} from "./model";

/** Questions listed before the link to the rest. */
const QUESTIONS_SHOWN = 5;

export function FixDrawer({
    task,
    group,
    issue,
    rec,
    activity,
    today,
    onClose,
    onSetStatus,
    onSetTarget,
    onOpenIssue,
    onOpenQuestions,
    onOpenDocs,
}: {
    task: PlacedTask;
    group: StaffStatus;
    /** The finding the task closes, when it came from one. */
    issue?: AuditIssue;
    /** The recommendation the task answers, when one is joined to it. */
    rec?: Recommendation;
    /** The plan's activity log; null while it loads or when there is none. */
    activity: AeoActivityEntry[] | null;
    today: string;
    onClose: () => void;
    onSetStatus: (t: TrackerTask, s: TaskStatus) => void;
    onSetTarget: (t: TrackerTask, date: string) => void;
    onOpenIssue?: (issueKey: string) => void;
    onOpenQuestions?: () => void;
    /** Open the Documents card on Client, where the client is asked now.
     *  Absent hides it (our own tasks, done work, or no Client screen here). */
    onOpenDocs?: () => void;
}) {
    const late = daysLate(task, today);
    const due = dueLabel(task);
    const why = whyLine(task, issue);
    const work = taskNoteText(task, issue);
    const moves = movesOf(task);
    const impact = impactOf(task, activity);
    const queries = task.sourceQueries ?? [];
    const pages = task.affectedPages ?? [];
    const contradiction = contradictionEvidence(task);
    const checkKey = task.source?.type === "check" ? task.source.ref : null;
    const done = task.status === "done";

    // Done work says when it finished (or just "Done"); open work says when
    // it is due, or how late it is in rose below.
    const sub = [done ? doneLabel(task) : TASK_STATUS[task.status].label, OWNER_LABEL[task.owner], blockLabel(task.block)];
    if (!done && !late && due) sub.push(due);

    return (
        <Drawer
            open
            onClose={onClose}
            title={task.action}
            sub={
                <span className="inline-flex flex-wrap items-center gap-x-1.5">
                    {/* The group's colour, so the drawer says who moves next
                        the way the row it came from did. */}
                    <Dot tone={STAFF_STATUS[group].tone} />
                    {sub.join(" · ")}
                    {late > 0 && (
                        <>
                            <span aria-hidden>·</span>
                            <span className={LATE_TEXT}>{lateLabel(late)}</span>
                        </>
                    )}
                </span>
            }
            foot={
                <>
                    <label className={cn(TYPE.tc, "flex items-center gap-2 text-gray-500")}>
                        Status
                        <StatusSelect value={task.status} onChange={(s) => onSetStatus(task, s)} label={`Status of ${task.action}`} />
                    </label>
                    <label className={cn(TYPE.tc, "flex items-center gap-1 text-gray-500")}>
                        Due
                        <TargetDate task={task} onSet={(d) => onSetTarget(task, d)} />
                    </label>
                    {(onOpenDocs || (checkKey && onOpenIssue)) && (
                        <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
                            {/* The way a fix reaches the client: the Google
                                Docs on Client (YW, 29 Sep 2026). There is no
                                "Needs the client" status to set, because the
                                backend has none. */}
                            {onOpenDocs && <ArrowLink onClick={onOpenDocs}>Open the documents</ArrowLink>}
                            {checkKey && onOpenIssue && (
                                <ArrowLink onClick={() => onOpenIssue(checkKey)}>Open in Site issues</ArrowLink>
                            )}
                        </span>
                    )}
                </>
            }
        >
            {(why || rec) && (
                <DrawerBlock label="Why">
                    {why && <p className="m-0">{why}</p>}
                    {/* The recommendation's own case for the work, when the
                        plan's step answers one: Overview's list folded in. */}
                    {rec && <p className={cn("m-0 text-gray-600", Boolean(why) && "mt-1")}>{rec.impact}</p>}
                </DrawerBlock>
            )}

            {work && work !== why && <DrawerBlock label="The work">{work}</DrawerBlock>}

            {moves.length > 0 && <DrawerBlock label="Moves">{moves.join(", ")}</DrawerBlock>}

            <DrawerBlock label="Impact check">
                {impact ? (
                    <>
                        <p className="m-0">{impact.text}</p>
                        {impact.at && <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>Recorded {shortDate(impact.at)}</p>}
                    </>
                ) : (
                    // Nothing measured yet, said plainly. The next scan
                    // re-measures the same check or question; no number is
                    // offered before it does.
                    <p className="m-0 text-gray-600">
                        {done ? "Re-measured at the next scan. Nothing is recorded against it yet." : "Measured at the next scan once it is done."}
                    </p>
                )}
            </DrawerBlock>

            {queries.length > 0 && (
                <DrawerBlock label="Questions it should move">
                    <ul className="list-plain m-0 p-0">
                        {queries.slice(0, QUESTIONS_SHOWN).map((q) => (
                            <li key={q} className="flex items-center gap-3 border-t border-gray-100 py-2 first:border-t-0">
                                <Dot tone="gray" />
                                <span className="min-w-0 flex-1">{q}</span>
                            </li>
                        ))}
                    </ul>
                    {(queries.length > QUESTIONS_SHOWN || onOpenQuestions) && (
                        <p className={cn(TYPE.tc, "m-0 mt-1 flex flex-wrap items-center gap-x-3 text-gray-500")}>
                            {queries.length > QUESTIONS_SHOWN && <span>And {queries.length - QUESTIONS_SHOWN} more</span>}
                            {onOpenQuestions && <ArrowLink onClick={onOpenQuestions}>Open Visibility</ArrowLink>}
                        </p>
                    )}
                </DrawerBlock>
            )}

            {task.gate && (
                // The gate is the point of the plan: how we prove the work is
                // genuinely done rather than just ticked.
                <DrawerBlock label="Verification gate">
                    <p className="m-0">{task.gate}</p>
                    {task.auto && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>Oura marks this done when the run completes.</p>}
                </DrawerBlock>
            )}

            {pages.length > 0 && (
                <DrawerBlock>
                    <AffectedPages task={task} issue={issue} onOpenIssue={onOpenIssue} />
                </DrawerBlock>
            )}

            {/* Both quotes, side by side: the whole value of the finding is
                that the merchant can read the clash for themselves (#3760). */}
            {contradiction && <ContradictionEvidenceBlock evidence={contradiction} />}
        </Drawer>
    );
}
